import React, { useState, useRef, useCallback } from 'react';
import { X, Upload, Scissors, Loader2 } from 'lucide-react';
import { useBgRemoval } from '../../hooks/useBgRemoval';
import { createStampPreset, removeBgAndSave, type StampPresetItem } from '../../services/api';
import { STATUS } from '../../styles/tokens';
import { RunningLoader } from '@/components/RunningLoader';

/** 棋盤格背景 */
const checkerboardStyle: React.CSSProperties = {
  backgroundImage: [
    'linear-gradient(45deg, #e2e8f0 25%, transparent 25%)',
    'linear-gradient(-45deg, #e2e8f0 25%, transparent 25%)',
    'linear-gradient(45deg, transparent 75%, #e2e8f0 75%)',
    'linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)',
  ].join(', '),
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
};

interface AddStampModalProps {
  onClose: () => void;
  onCreated: (preset: StampPresetItem) => void;
}

const AddStampModal: React.FC<AddStampModalProps> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [removeBg, setRemoveBg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const bgRemoval = useBgRemoval();

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
    setError(null);
    // 如果已勾選去背，自動開始處理
    if (removeBg) {
      bgRemoval.process(f);
    }
    e.target.value = '';
  }, [removeBg, bgRemoval]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (!f || !f.type.startsWith('image/')) return;
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
    setError(null);
    if (removeBg) {
      bgRemoval.process(f);
    }
  }, [removeBg, bgRemoval]);

  const handleToggleRemoveBg = useCallback(() => {
    const next = !removeBg;
    setRemoveBg(next);
    if (next && file) {
      bgRemoval.process(file);
    } else if (!next) {
      bgRemoval.reset();
    }
  }, [removeBg, file, bgRemoval]);

  const handleSave = async () => {
    if (!name.trim()) { setError('請輸入印章名稱'); return; }
    if (!file) { setError('請上傳圖片'); return; }

    setSaving(true);
    setError(null);

    try {
      let preset: StampPresetItem;

      if (removeBg && bgRemoval.status === 'done' && bgRemoval.resultBlob) {
        // 去背已完成，直接用去背結果儲存，不重複呼叫去背 API
        const removedFile = new File(
          [bgRemoval.resultBlob],
          file.name.replace(/\.[^.]+$/, '.png'),
          { type: 'image/png' },
        );
        preset = await createStampPreset(name.trim(), removedFile);
      } else if (removeBg) {
        // 去背尚未完成（理論上 disabled 會擋住，保留作為 fallback）
        preset = await removeBgAndSave(name.trim(), file);
      } else {
        // 直接上傳原圖
        preset = await createStampPreset(name.trim(), file);
      }

      onCreated(preset);
      onClose();
    } catch (e: any) {
      setError(e.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  // 判斷去背是否正在處理
  const isBgProcessing = removeBg && bgRemoval.status === 'processing';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-5 w-[90vw] max-w-lg flex flex-col gap-4"
        onClick={e => e.stopPropagation()}
      >
        {/* 標題 */}
        <div className="flex items-center justify-between">
          <span className="font-bold text-slate-800 dark:text-white text-sm">新增自訂印章</span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* 名稱 */}
        <div>
          <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">印章名稱</label>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="例如：FB 粉專連結"
            className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* 上傳區 */}
        {!file ? (
          <div
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-indigo-400 dark:hover:border-indigo-500 rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer transition-colors"
          >
            <Upload size={24} className="text-slate-400" />
            <span className="text-xs text-slate-500 dark:text-slate-400">點擊或拖曳圖片至此</span>
            <span className="text-[10px] text-slate-400">JPG / PNG / WebP（10MB 內）</span>
          </div>
        ) : (
          <div className="flex gap-3 items-start">
            {/* 原圖預覽 */}
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-28 h-28 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                <img src={filePreview!} alt="原圖" className="w-full h-full object-contain" />
              </div>
              <span className="text-[10px] text-slate-400">原圖</span>
            </div>

            {/* 去背結果預覽 */}
            {removeBg && (
              <>
                <div className="flex items-center justify-center h-28 text-slate-300">→</div>
                <div className="flex flex-col items-center gap-1.5">
                  <div className="w-28 h-28 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700" style={checkerboardStyle}>
                    {bgRemoval.status === 'processing' && (
                      <div className="w-full h-full flex items-center justify-center text-slate-600 dark:text-slate-300">
                        <RunningLoader size="sm" label="處理中" />
                      </div>
                    )}
                    {bgRemoval.status === 'done' && bgRemoval.resultUrl && (
                      <img src={bgRemoval.resultUrl} alt="去背結果" className="w-full h-full object-contain" />
                    )}
                    {bgRemoval.status === 'error' && (
                      <div className="w-full h-full flex items-center justify-center text-red-400 text-[10px] p-1 text-center">
                        去背失敗
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-slate-400">去背結果</span>
                </div>
              </>
            )}

            {/* 換圖按鈕 */}
            <button
              onClick={() => { setFile(null); setFilePreview(null); bgRemoval.reset(); }}
              className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 mt-8 shrink-0"
            >
              換圖
            </button>
          </div>
        )}

        {/* 去背選項 */}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <button
            onClick={handleToggleRemoveBg}
            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
              removeBg
                ? 'bg-indigo-600 border-indigo-600 text-white'
                : 'border-slate-300 dark:border-slate-600'
            }`}
          >
            {removeBg && <Scissors size={12} />}
          </button>
          <span className="text-xs text-slate-600 dark:text-slate-400">先進行去背處理</span>
          {isBgProcessing && <Loader2 size={12} className="animate-spin text-indigo-500" />}
        </label>

        {/* 錯誤訊息 */}
        {error && (
          <p className={`text-xs ${STATUS.error} rounded-lg px-3 py-2`}>{error}</p>
        )}

        {/* 操作按鈕 */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-sm font-medium rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim() || !file || isBgProcessing}
            className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-1.5"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            {saving ? '儲存中...' : '儲存印章'}
          </button>
        </div>

        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      </div>
    </div>
  );
};

export default AddStampModal;
